/**
 * Optional mirror of leads to Google Sheets via a deployed Google Apps Script web app URL.
 *
 * Set in `.env`:
 * - REACT_APP_GOOGLE_SHEETS_WEBAPP_URL — Apps Script deployment URL (ends with /exec)
 * - REACT_APP_GOOGLE_SHEETS_INGEST_SECRET (optional) — must match verifying code in Apps Script
 *
 * Submission uses fetch + `no-cors` + `text/plain` body so the browser avoids a JSON preflight.
 * Rows are appended best-effort; failures never surface to the user.
 */

/** @typedef {{ form?: string, summary?: string, detail?: unknown }} LeadSheetPayload */

/**
 * CRA + Webpack inline `process.env.REACT_APP_*` only when keys are referenced as static
 * identifiers. Dynamic lookups like `process.env[name]` are not replaced and stay empty.
 */
function getGoogleSheetsWebAppUrl() {
  try {
    /** CRA/Webpack replaces `process.env.REACT_APP_*` at compile time; do not return early based on `process` — client bundles may not define `process.env`, which made the guard always bail out. */
    const react = process.env.REACT_APP_GOOGLE_SHEETS_WEBAPP_URL;
    if (react) return String(react).trim();
    const vite = process.env.VITE_GOOGLE_SHEETS_WEBAPP_URL;
    if (vite) return String(vite).trim();
  } catch {
    /* ignore */
  }
  return '';
}

function getGoogleSheetsIngestSecret() {
  try {
    const react = process.env.REACT_APP_GOOGLE_SHEETS_INGEST_SECRET;
    if (react) return String(react).trim();
    const vite = process.env.VITE_GOOGLE_SHEETS_INGEST_SECRET;
    if (vite) return String(vite).trim();
  } catch {
    /* ignore */
  }
  return '';
}

function getReleaseTag() {
  try {
    const react = process.env.REACT_APP_RELEASE_TAG;
    if (react) return String(react).trim();
    const vite = process.env.VITE_RELEASE_TAG;
    if (vite) return String(vite).trim();
  } catch {
    /* ignore */
  }
  return '';
}

/** True when `REACT_APP_GOOGLE_SHEETS_WEBAPP_URL` (or Vite equivalent) is set. */
export function isGoogleSheetsConfigured() {
  return Boolean(getGoogleSheetsWebAppUrl());
}

function toCleanString(value) {
  if (value == null) return '';
  return String(value).trim();
}

function toStringArray(value) {
  if (Array.isArray(value)) {
    return value.map((v) => toCleanString(v)).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((v) => toCleanString(v))
      .filter(Boolean);
  }
  return [];
}

function normalizeServiceName(value) {
  const text = toCleanString(value);
  if (!text) return '';
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

const STRIP_DETAIL_KEYS = new Set([
  // Backend-only operational fields
  'referral_id',
  'referral_entity',
  'refund_amount',
  'rm_id',
  'op_id',
  'tag',

  // Campaign / device tracking fields (not needed in Sheets lead rows)
  'capture_page_path',
  'capture_page_url',
  'capture_page_query',
  'capture_referrer_url',
  'platform',
  'device_type',
  'device_model',
  'os_name',
  'os_version',
  'browser_name',
  'browser_version',
  'app_version',
  'environment',
  'user_agent',
  'viewport_width',
  'viewport_height',
  'screen_width',
  'screen_height',
  'capture_language',
  'timezone_offset_min',
  'ingestion_source',
]);

/**
 * Normalize equivalent fields from different forms to shared key names.
 * Unique per-form fields are kept untouched.
 */
function normalizeDetailKeys(obj, sourceForm) {
  const clientName = toCleanString(obj.client_name || obj.full_name || obj.your_name || obj.name);
  if (clientName) obj.client_name = clientName;

  const mobile = toCleanString(obj.mobile || obj.phone_number || obj.phone);
  if (mobile) obj.mobile = mobile;

  const emailId = toCleanString(obj.email_id || obj.email_address || obj.email);
  if (emailId) obj.email_id = emailId;

  const remarks = toCleanString(obj.remarks || obj.remark || obj.your_message || obj.message);
  if (remarks) obj.remarks = remarks;

  const serviceCodes = toStringArray(
    Array.isArray(obj.service_required) ? obj.service_required : obj.services_required,
  );
  const serviceLabelText = toCleanString(
    typeof obj.service_required === 'string' ? obj.service_required : obj.service_required_label,
  );
  if (serviceCodes.length > 0) {
    obj.service_required = serviceCodes;
  } else if (serviceLabelText) {
    // Keep a single canonical key for list-like field.
    obj.service_required = toStringArray(serviceLabelText);
  }

  const selectedServices = serviceCodes.length > 0
    ? serviceCodes.map(normalizeServiceName).filter(Boolean)
    : [];

  // Explicit source-based service labels where requested.
  if (sourceForm === 'referral') {
    obj.service = 'referral';
  } else if (sourceForm === 'contact') {
    obj.service = 'contact support';
  } else if (sourceForm === 'home_services_multi') {
    obj.service = 'multiple services';
  // Otherwise keep service as explicit selected service name(s).
  } else if (selectedServices.length === 1) {
    obj.service = selectedServices[0];
  } else if (selectedServices.length > 1) {
    obj.service = selectedServices;
  } else if (sourceForm === 'home_itr_mini') {
    obj.service = 'itr_filing';
  } else if (sourceForm === 'income_tax') {
    obj.service = normalizeServiceName(obj.service_code) || 'income_tax';
  } else if (!obj.service) {
    obj.service = null;
  }

  const referralPhones = toStringArray(obj.referral_phone_numbers || obj.referal_phone_number);
  if (referralPhones.length > 0) obj.referral_phone_numbers = referralPhones;

  const financialYears = toStringArray(obj.financial_years || obj.financial_year);
  if (financialYears.length > 0) {
    obj.financial_year = financialYears;
  }

  const incomeSources = toStringArray(obj.sources_of_income || obj.source_of_income);
  if (incomeSources.length > 0) {
    obj.source_of_income = incomeSources;
  }

  // Keep `release_tag` across all forms; fallback to env value when available.
  const releaseTag = toCleanString(obj.release_tag || getReleaseTag());
  obj.release_tag = releaseTag || null;

  // Remove alias keys once canonical names are available.
  delete obj.full_name;
  delete obj.your_name;
  delete obj.name;
  delete obj.phone_number;
  delete obj.phone;
  delete obj.email;
  delete obj.email_address;
  delete obj.remark;
  delete obj.your_message;
  delete obj.message;
  delete obj.service_required_label;
  delete obj.services_required;
  delete obj.referal_phone_number;
  delete obj.financial_years;
  delete obj.sources_of_income;
  delete obj.service_code;
  delete obj.detail_json;
}

function stripUnwantedDetailKeys(obj) {
  for (const key of STRIP_DETAIL_KEYS) {
    if (key in obj) delete obj[key];
  }
}

/** Redact secrets before storing in Sheets. */
function sanitizeDetail(detail, sourceForm) {
  if (detail == null) return '';
  let obj;
  try {
    obj = typeof detail === 'string' ? JSON.parse(detail) : JSON.parse(JSON.stringify(detail));
  } catch {
    return typeof detail === 'string' ? detail : '[unserializable]';
  }
  if (obj && typeof obj === 'object') {
    normalizeDetailKeys(obj, sourceForm);
    stripUnwantedDetailKeys(obj);

    if ('password' in obj && obj.password) obj.password = '[redacted]';
    if ('pwd' in obj && obj.pwd) obj.pwd = '[redacted]';
  }
  return JSON.stringify(obj);
}

/**
 * @param {LeadSheetPayload} row
 */
export function appendLeadToGoogleSheets(row) {
  const url = getGoogleSheetsWebAppUrl();
  if (!url) return;

  const secret = getGoogleSheetsIngestSecret();

  const envelope = {
    ...(secret ? { secret } : {}),
    timestamp: new Date().toISOString(),
    // Keep these empty as requested; use `detail` for stored submission data.
    form: '',
    summary: '',
    detail: sanitizeDetail(row.detail, row.form),
  };

  try {
    fetch(url, {
      method: 'POST',
      mode: 'no-cors',
      cache: 'no-store',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(envelope),
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}
